import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const ROLE_LABELS = { owner: 'Owner', admin: 'Org Admin', member: 'Member' };

const roleLabel = (role) => ROLE_LABELS[role] || 'Member';

/**
 * Slack-like workspace picker shown after login (or when switching workspaces)
 * when the user belongs to more than one organization.
 */
const OrgPicker = () => {
  const navigate = useNavigate();
  const { organizations, setActiveOrgId, refreshOrganizations, logout, user } = useAuth();
  const [refreshing, setRefreshing] = useState(true);

  useEffect(() => {
    let cancelled = false;
    refreshOrganizations().then(() => {
      if (!cancelled) setRefreshing(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshOrganizations]);

  // Single workspace → skip the picker entirely.
  useEffect(() => {
    if (!refreshing && organizations.length === 1) {
      setActiveOrgId(organizations[0].id);
      navigate('/', { replace: true });
    }
  }, [refreshing, organizations, setActiveOrgId, navigate]);

  const openWorkspace = (orgId) => {
    setActiveOrgId(orgId);
    navigate('/', { replace: true });
  };

  const continueWithoutOrg = () => {
    setActiveOrgId(null);
    navigate('/', { replace: true });
  };

  if (refreshing) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f4f2ec' }}>
        <CircularProgress size={28} sx={{ color: '#2a52be' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 1 }}>
          {organizations.length > 0 ? 'Choose a workspace' : 'Welcome to PRA Machine'}
        </Typography>
        <Typography sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#838996' }}>
          {organizations.length > 0
            ? `You're a member of ${organizations.length} organizations. Pick one to get started.`
            : 'You’re signed in, but not a member of any organization yet.'}
        </Typography>
      </Box>

      {organizations.length > 0 ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {organizations.map((org) => (
            <Card
              key={org.id}
              elevation={0}
              sx={{ border: '1px solid #e0ddd6', borderRadius: 2, backgroundColor: '#ffffff', '&:hover': { borderColor: '#2a52be', boxShadow: '0 4px 16px rgba(42,82,190,0.12)' } }}
            >
              <CardActionArea onClick={() => openWorkspace(org.id)} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ color: '#2a52be' }} />
                      <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', fontSize: '1.05rem' }}>
                        {org.name}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={roleLabel(org.my_role)}
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        backgroundColor: org.my_role === 'owner' ? 'rgba(42,82,190,0.1)' : org.my_role === 'admin' ? 'rgba(0,7,94,0.08)' : 'rgba(131,137,150,0.12)',
                        color: org.my_role === 'owner' ? '#2a52be' : org.my_role === 'admin' ? '#00075e' : '#838996',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#838996', fontSize: '0.85rem', mb: 1.5, minHeight: 40 }}>
                    {org.description || 'No description provided.'}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', fontSize: '0.8rem', fontWeight: 600, color: '#2a52be', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Open workspace <ArrowForwardIcon fontSize="small" />
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2, backgroundColor: '#ffffff', border: '1px solid #e0ddd6', textAlign: 'center' }}>
          <Typography sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#00075e', mb: 1 }}>
            Organizations are invite-only. Ask an organization owner to send you an invite link, or ask the platform administrator to add you.
          </Typography>
          <Typography sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#838996', fontSize: '0.85rem', mb: 3 }}>
            Already have an invite link? Open it in this browser and you'll join automatically.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={continueWithoutOrg} sx={{ textTransform: 'none', color: '#2a52be', borderColor: '#2a52be' }}>
              Continue to my requests
            </Button>
            <Button variant="text" onClick={logout} sx={{ textTransform: 'none', color: '#838996' }}>
              Sign out
            </Button>
          </Box>
        </Paper>
      )}

      {user && organizations.length > 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button variant="text" onClick={logout} sx={{ textTransform: 'none', color: '#838996', fontSize: '0.8rem' }}>
            Sign out ({user.email})
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default OrgPicker;
